/**
 * Landing v3 — the six product tiles (Figma 118:1538, user 2026-09-29): 100%
 * generated from your sources · Share & Study with your friends (the first
 * row, new) · Compare your stats with friends (leaderboard) · Motivates u
 * everyday (heatmap) · Keep track of your study time (timer) · 98% improved
 * their grades (team copy 2026-10-02; it was "x2 your grades"). Two 530px columns, 20px
 * gutters, heights from content so the columns stagger the way the frame
 * does; a phone reads them row by row. DS tokens and DS components, no
 * navigation; everything is display only except the leaderboard's Global /
 * University toggle and the study timer, which work.
 */
import {
  Button,
  OVERLAY_DESC_CLASS,
  OVERLAY_SURFACE,
  surfaceShape,
  TIP_ARROW_SIZE,
  TipArrow,
} from '@flashka/shared/ui';

import { motion, useInView, useReducedMotion } from 'framer-motion';
import {
  ChartNoAxesCombinedIcon,
  CirclePlusIcon,
  FileCogIcon,
  FlameIcon,
  type LucideIcon,
  Maximize2Icon,
  PauseIcon,
  RotateCcwIcon,
  Settings2Icon,
  ShareIcon,
  SkipForwardIcon,
  TimerIcon,
  TrophyIcon,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CycleDots } from '@/components/pomodoro/cycle-dots';
import { FOCUS_SESSIONS_PER_LONG_BREAK } from '@/components/pomodoro/pomodoro-store';
import { cn } from '@/lib/utils';

import { GLASS_PILL_CLASS, LogoMark } from './landing-chrome';
import { FriendsGroups } from './landing-friends';
import { BADGE_TONES, type BadgeHue } from './landing-hues';
import { LeaderboardWidget } from './landing-leaderboard';
import { useCardPan } from './landing-pan';
import { SourcesDiagram } from './landing-sources';

/**
 * The tile surface (Figma 48:21548): `#fafafa` at 90% over the DS `Glass`
 * style at radius 27, a 1px `secondary-200` border, radius 40 — no drop
 * shadow on these, unlike the chrome.
 */
// Figma 48:21190: radius 64, 24px top/bottom, the header block padded 40 so
// the icon sits 64px under the tile's edge; the top bar's glass (user
// decision) so the page's dots show through. The frame's children run 469
// wide inside a 530 tile — 30.5px a side — which its declared 40px padding
// contradicts; the visual truth wins.
const TILE_CLASS = `flex flex-col items-center gap-6 rounded-[64px] px-[30px] py-6 ${GLASS_PILL_CLASS}`;
/**
 * The phone layout's tile (user 2026-09-28): a near-opaque DS fill instead of
 * the glass — over the plain dotted page the blur only ever averaged the dots,
 * and on a phone it is a full-tile repaint for every frame of scroll — and a
 * shape sized for a 390px screen.
 */
const STACKED_TILE_CLASS =
  'flex flex-col items-center gap-5 rounded-[40px] border border-secondary-200 bg-secondary-50/90 px-3 py-5 shadow-card-row';

/** `desktop` = two 530px columns (the frame); `stacked` = the phone layout: one
 *  column, two from a tablet's width. */
export type TilesLayout = 'desktop' | 'stacked';

// Re-exported for the pages that already import them from here.
export { BADGE_TONES, type BadgeHue };

/** Each tile's accent; the title itself is one grey. */
const TILE_HUES = {
  sources: 'secondary',
  friends: 'cyan',
  rank: 'amber',
  heat: 'primary',
  timer: 'pink',
  grades: 'green',
} as const satisfies Record<string, BadgeHue>;
/** Each tile's icon badge colours. */
const TONES = {
  sources: BADGE_TONES[TILE_HUES.sources],
  friends: BADGE_TONES[TILE_HUES.friends],
  rank: BADGE_TONES[TILE_HUES.rank],
  heat: BADGE_TONES[TILE_HUES.heat],
  timer: BADGE_TONES[TILE_HUES.timer],
  grades: BADGE_TONES[TILE_HUES.grades],
};

/** The tiles' icon badge: 46.8px (a 24px glyph in 11.4px of padding), r16. */
export function IconBadge({ icon: Icon, tone }: { icon: LucideIcon; tone: string }) {
  return (
    <span
      className={cn(
        'flex items-center justify-center rounded-2xl border p-[11px] shadow-[0_2px_6px_rgba(0,0,0,0.06)]',
        tone,
      )}>
      <Icon className="h-6 w-6" aria-hidden />
    </span>
  );
}

/** Icon badge (46.8px: 24 icon + 11.4 padding, r16) + the 28px title, 13px apart. */
function TileHeader({
  icon: Icon,
  tone,
  title,
  mark,
  wide = false,
}: {
  icon: LucideIcon;
  /** The badge's colours, from `TONES`. */
  tone: string;
  title: string;
  /** A footnote mark after the title (the grades claim's `*`). */
  mark?: string;
  /** A 400px title instead of 305px: the grades claim ("98% improved their
   *  grades and procrastinated less") fits two lines in every language
   *  (user 2026-10-02). */
  wide?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-[13px] py-10 text-center">
      <IconBadge icon={Icon} tone={tone} />
      {/* D3: 28/36 bold, secondary-700 — the whole title one grey, no
          accent words (Figma 104:1302, user 2026-09-25). */}
      {/* An H2: the tiles are top-level sections of the page, not parts of
          the last feature card above them. */}
      <h2
        className={cn(
          'text-[28px] font-bold leading-9 text-secondary-700',
          wide ? 'max-w-[400px]' : 'max-w-[305px]',
        )}>
        {title}
        {mark}
      </h2>
    </div>
  );
}

/** The inner widget chrome the frame reuses: a `secondary-100` tray with a
 *  small header row and a white `secondary-50` body. */
function WidgetTray({
  icon: Icon,
  title,
  right,
  children,
  className,
}: {
  icon: LucideIcon;
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-1.5 rounded-[36px] bg-secondary-100 px-1 pb-1 pt-1.5',
        className,
      )}>
      <div className="flex items-center justify-between py-2.5 pl-4 pr-3">
        <span className="flex items-center gap-2 text-[14px] font-bold text-secondary-600">
          <Icon className="h-5 w-5" aria-hidden />
          {title}
        </span>
        {right}
      </div>
      <div className="rounded-[32px] bg-secondary-50 p-4">{children}</div>
    </div>
  );
}

// ── Heatmap (Figma 48:21320): three blocks of 7 × 10 cells of 12px, gap 4 ──

/** Deterministic sample activity so the tile never renders empty. */
const HEAT_LEVELS = [
  'bg-secondary-200',
  'bg-primary-200',
  'bg-primary-400',
  'bg-primary-600',
];
const heatLevel = (block: number, i: number) => {
  const v = (i * 7 + block * 11 + ((i * i) % 5)) % 10;
  return v < 4 ? 0 : v < 7 ? 1 : v < 9 ? 2 : 3;
};

/**
 * Each studied day's cell twinkles softly, for ever (user 2026-09-25): a slow
 * dip in opacity on its own period and phase, so the grid shimmers rather
 * than pulses in unison. Deterministic, so every visit shimmers the same way.
 * The empty (grey) days stay still. Paused off screen; still under reduced
 * motion (`.lv3-twinkle`, index.css).
 */
const twinkle = (block: number, i: number) => {
  const k = (block * 70 + i) * 2654435761;
  return {
    animationDuration: `${2.6 + ((k >>> 3) % 26) / 10}s`,
    animationDelay: `-${((k >>> 7) % 50) / 10}s`,
  };
};

function MiniHeatmap({ blocks = 3 }: { blocks?: number }) {
  const { t } = useTranslation();
  const letters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const ref = useRef<HTMLDivElement>(null);
  const near = useInView(ref, { margin: '200px 0px' });
  return (
    <div
      ref={ref}
      className="flex justify-center gap-4"
      aria-label={t('landingV3.tiles.labels.heatmap')}>
      {Array.from({ length: blocks }, (_, block) => (
        <div key={block} className="flex flex-col gap-2 py-2">
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 70 }, (_, i) => {
              const level = heatLevel(block, i);
              return (
                <span
                  key={i}
                  className={cn(
                    'h-3 w-3 rounded-[3px]',
                    HEAT_LEVELS[level],
                    level > 0 && 'lv3-twinkle',
                  )}
                  style={
                    level > 0
                      ? {
                          ...twinkle(block, i),
                          animationPlayState: near ? 'running' : 'paused',
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {letters.map((l, i) => (
              <span
                key={i}
                className="w-3 text-center text-[8px] font-semibold leading-[10px] text-secondary-300">
                {l}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Grades (Figma 104:1297): two bars, growing up ──────────────────────────

/** The chart's hairlines: 60px apart from 131px down, as the frame draws them. */
const GRID_TOPS = [131, 191, 251, 311, 371];
const GROW_EASE = [0.22, 1, 0.36, 1] as const;

/**
 * The grades tile ("98% improved their grades…"): a short grey bar "With other AIs" and a green bar twice
 * its height crowned by the Flashka mark, on faint hairlines, their feet lost
 * in a fade to the page. When the tile arrives the bars GROW UP from the
 * baseline — grey first, green after and taller — then the tooltip and the
 * mark settle on top. The bars rise from below a clip (transform only), so
 * their rounded tops never squash.
 */
function GradesChart({ play }: { play: boolean }) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const shown = play || !!reduceMotion;
  const grow = (delay: number) => ({
    initial: reduceMotion ? false : { y: '100%' },
    animate: shown ? { y: '0%' } : undefined,
    transition: { duration: 1.1, delay, ease: GROW_EASE },
  });
  const settle = (delay: number) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 8 },
    animate: shown ? { opacity: 1, y: 0 } : undefined,
    transition: { duration: 0.45, delay, ease: GROW_EASE },
  });
  return (
    <div className="relative min-h-[354px] w-full flex-1 overflow-clip">
      {GRID_TOPS.map((top) => (
        <span
          key={top}
          aria-hidden
          className="absolute inset-x-0 h-px bg-secondary-100"
          style={{ top }}
        />
      ))}
      {/* Grey: with other AIs (Figma 104:1310). */}
      <motion.div
        {...grow(0.15)}
        aria-hidden
        className="absolute -bottom-16 left-[calc(50%-90px)] top-[227px] w-[60px] rounded-2xl bg-secondary-200"
      />
      {/* Green: with Flashka (Figma 104:1309), twice as tall. */}
      <motion.div
        {...grow(0.4)}
        aria-hidden
        className="absolute -bottom-16 left-[calc(50%+30px)] top-[71px] w-[60px] rounded-2xl bg-green-500 shadow-[0px_1.5px_12.5px_0px_rgba(5,223,114,0.2)]"
      />
      {/* The DS tooltip surface and Tip Arrow, shown as a label (Figma
          104:1314), centred on the grey bar. */}
      {/* Centred with framer's `x`: its inline transform (the settle's `y`)
          would override a Tailwind translate class. */}
      <motion.div
        {...settle(1.0)}
        style={{ x: '-50%' }}
        className="absolute left-[calc(50%-60px)] top-[181px] flex flex-col items-center">
        <span
          className={cn(
            OVERLAY_SURFACE,
            surfaceShape(false),
            OVERLAY_DESC_CLASS,
            'whitespace-nowrap shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)]',
          )}>
          {t('landingV3.tiles.grades.otherAis')}
        </span>
        <TipArrow
          aria-hidden
          width={TIP_ARROW_SIZE.width}
          height={TIP_ARROW_SIZE.height}
          className="-mt-px"
        />
      </motion.div>
      {/* The Flashka mark on the green bar (Figma 104:1311). */}
      <motion.div
        {...settle(1.25)}
        aria-hidden
        style={{ x: '-50%' }}
        className="absolute left-[calc(50%+60px)] top-[31px] drop-shadow-[0px_16px_12px_rgba(0,0,0,0.1)]">
        <LogoMark className="h-8 w-[30px]" />
      </motion.div>
      {/* The bars' feet fade into the page (Figma 104:1431). */}
      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-[72px] bg-gradient-to-b from-transparent to-background"
      />
    </div>
  );
}

// ── Study timer (Figma 48:21561): ring + cycle dots + controls ─────────────

/** The session: a five-minute focus countdown (user 2026-09-25). */
const TIMER_SECONDS = 5 * 60;

const clock = (sec: number) =>
  `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

/**
 * The study timer, WORKING (user 2026-09-25): a five-minute focus countdown
 * that starts when the tile comes into view. A secondary Pause stops it and
 * Start resumes it; reset puts it back to 05:00; skip — or reaching 00:00 —
 * banks the session in the cycle dots and loads the next five minutes, paused
 * at 00:00's end like a real timer. Local state only — nothing is saved. Time
 * comes from timestamps, so it stays right however the page is scrolled; it
 * only re-renders while it is on screen.
 */
function MiniTimer({ controlSize = 'xs' }: { controlSize?: 'xs' | 'm' }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const near = useInView(ref, { margin: '200px 0px' });
  const [running, setRunning] = useState(true);
  /** Seconds already spent before the current run, and when that run started. */
  const [spent, setSpent] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [completed, setCompleted] = useState(0);
  const [, setTick] = useState(0);

  // The clock starts the first time the tile is near the screen.
  useEffect(() => {
    if (running && near && startedAt === null) setStartedAt(Date.now());
  }, [running, near, startedAt]);
  // One repaint a second while it runs on screen.
  useEffect(() => {
    if (!running || !near) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [running, near]);

  const elapsed =
    spent +
    (running && startedAt !== null ? Math.floor((Date.now() - startedAt) / 1000) : 0);
  const remaining = Math.max(0, TIMER_SECONDS - elapsed);
  const bank = () => setCompleted((c) => (c + 1) % (FOCUS_SESSIONS_PER_LONG_BREAK + 1));

  // Time's up: bank the session, load the next five minutes, and stop.
  useEffect(() => {
    if (remaining > 0) return;
    setCompleted((c) => (c + 1) % (FOCUS_SESSIONS_PER_LONG_BREAK + 1));
    setSpent(0);
    setStartedAt(null);
    setRunning(false);
  }, [remaining]);

  const pause = () => {
    setSpent(elapsed);
    setStartedAt(null);
    setRunning(false);
  };
  const start = () => {
    setStartedAt(Date.now());
    setRunning(true);
  };
  const reset = () => {
    setSpent(0);
    setStartedAt(running ? Date.now() : null);
  };
  const skip = () => {
    bank();
    reset();
  };
  const r = 55;
  const c = 2 * Math.PI * r;
  return (
    <div ref={ref} className="flex flex-col items-center gap-2">
      <div className="relative h-[122px] w-[122px]">
        <svg viewBox="0 0 122 122" className="h-full w-full -rotate-90" aria-hidden>
          <circle
            cx="61"
            cy="61"
            r={r}
            className="stroke-pink-100"
            strokeWidth="6"
            fill="none"
          />
          <circle
            cx="61"
            cy="61"
            r={r}
            className="stroke-pink-500 transition-[stroke-dashoffset] duration-1000 ease-linear"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - remaining / TIMER_SECONDS)}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            role="timer"
            className="text-2xl font-bold tabular-nums leading-7 text-pink-600">
            {clock(remaining)}
          </span>
          <span className="text-[12px] font-semibold leading-[14px] text-pink-300">
            {t('landingV3.tiles.labels.focus')}
          </span>
        </div>
      </div>
      {/* The current dot pulses only near the screen: an animation running
          off screen keeps the browser restyling every animated element on
          the page, every frame. */}
      <CycleDots
        sessionType="focus"
        completedFocus={completed}
        running={running && near}
      />
      {/* The frame's controls, the DS buttons — working now. */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size={controlSize}
          icon={RotateCcwIcon}
          accessibilityLabel={t('landingV3.tiles.labels.reset')}
          onPress={reset}
        />
        {running ? (
          <Button
            variant="secondary"
            size={controlSize}
            leftIcon={PauseIcon}
            onPress={pause}>
            {t('landingV3.tiles.labels.pause')}
          </Button>
        ) : (
          <Button
            variant="primary"
            size={controlSize}
            leftIcon={CirclePlusIcon}
            onPress={start}>
            {t('landingV3.tiles.labels.start')}
          </Button>
        )}
        <Button
          variant="ghost"
          size={controlSize}
          icon={SkipForwardIcon}
          accessibilityLabel={t('landingV3.tiles.labels.skip')}
          onPress={skip}
        />
      </div>
    </div>
  );
}

// ── The section ────────────────────────────────────────────────────────────

/** How far behind the left column the right one arrives, s. */
const COLUMN_BEAT = 0.18;

/**
 * One tile and its entrance (user: "they should appear one after another with
 * a smooth animation"). Each tile rises in when IT reaches the viewport — not
 * when the block does — so the lower row is still to come as you scroll; and
 * the right column trails the left by a beat, so two tiles that enter together
 * still arrive one after the other. `children` gets the tile's own "arrived",
 * for content that should start with it (the leaderboard's rows).
 */
/** The tiles take the cards' hover, gentler: their widgets are live (a toggle,
 *  a timer), and a card that leaned much would move them under the cursor. */
const TILE_PAN = { tiltDeg: 2, lightAlpha: 0.08 } as const;

function Tile({
  column,
  hue,
  layout,
  className,
  children,
}: {
  column: 0 | 1;
  /** The tile's accent (its badge's hue), for the hover light. */
  hue: BadgeHue;
  layout: TilesLayout;
  className?: string;
  children: (arrived: boolean) => ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const arrived = useInView(ref, { once: true, amount: 0.2 });
  const reduceMotion = useReducedMotion();
  const pan = useCardPan(!!reduceMotion, { hue, ...TILE_PAN });
  return (
    <motion.article
      ref={ref}
      {...pan.handlers}
      style={pan.tilt}
      initial={reduceMotion ? false : { opacity: 0, y: 56, scale: 0.97 }}
      animate={arrived || reduceMotion ? { opacity: 1, y: 0, scale: 1 } : undefined}
      transition={{
        duration: 0.8,
        delay: column * COLUMN_BEAT,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={cn(
        'relative',
        layout === 'stacked' ? STACKED_TILE_CLASS : TILE_CLASS,
        className,
      )}>
      {children(arrived || !!reduceMotion)}
      {pan.light}
    </motion.article>
  );
}

/** The phone's reading order (the frame's rows): sources, friends, rank,
 *  heat, timer, grades. Literal classes, for Tailwind to find. */
const ORDER: Record<number, string> = {
  1: 'order-1',
  2: 'order-2',
  3: 'order-3',
  4: 'order-4',
  5: 'order-5',
  6: 'order-6',
};

/** Each tile title's parts, in order, as whole i18n keys (only the parts
 *  the locales carry). */
const TILE_TITLE_KEYS = {
  sources: ['landingV3.tiles.sources.pre'],
  rank: [
    'landingV3.tiles.rank.pre',
    'landingV3.tiles.rank.hl',
    'landingV3.tiles.rank.post',
  ],
  timer: [
    'landingV3.tiles.timer.pre',
    'landingV3.tiles.timer.hl',
    'landingV3.tiles.timer.mid',
    'landingV3.tiles.timer.hl2',
  ],
  friends: ['landingV3.tiles.friends.pre'],
  heat: [
    'landingV3.tiles.heat.pre',
    'landingV3.tiles.heat.hl',
    'landingV3.tiles.heat.post',
  ],
  grades: [
    'landingV3.tiles.grades.pre',
    'landingV3.tiles.grades.hl',
    'landingV3.tiles.grades.mid',
  ],
} as const;

export function TilesSection({ layout = 'desktop' }: { layout?: TilesLayout }) {
  const { t } = useTranslation();
  const stacked = layout === 'stacked';
  // The titles are stored in parts (they once coloured their key words);
  // joined, they read as one line.
  const title = (key: keyof typeof TILE_TITLE_KEYS) =>
    TILE_TITLE_KEYS[key].map((part) => t(part, { defaultValue: '' })).join('');
  // A phone reads the tiles row by row: below a tablet's width the columns
  // dissolve (`contents`) and each tile's `order` puts it in its row.
  const column = stacked
    ? 'contents md:flex md:flex-col md:gap-5'
    : 'flex flex-col gap-5';
  const order = (n: number) => (stacked ? ORDER[n] : undefined);
  return (
    <div
      className={cn(
        'grid w-full max-w-[1080px] gap-5',
        stacked ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-2',
      )}>
      {/* Column 1 */}
      <div className={column}>
        <Tile hue={TILE_HUES.sources} column={0} layout={layout} className={order(1)}>
          {(arrived) => (
            <>
              <TileHeader
                icon={FileCogIcon}
                tone={TONES.sources}
                title={title('sources')}
              />
              <SourcesDiagram play={arrived} />
            </>
          )}
        </Tile>
        <Tile hue={TILE_HUES.rank} column={0} layout={layout} className={order(3)}>
          {(arrived) => (
            <>
              <TileHeader icon={TrophyIcon} tone={TONES.rank} title={title('rank')} />
              <LeaderboardWidget
                play={arrived}
                className="max-w-none"
                compact={stacked}
              />
            </>
          )}
        </Tile>
        <Tile hue={TILE_HUES.timer} column={0} layout={layout} className={order(5)}>
          {() => (
            <>
              <TileHeader icon={TimerIcon} tone={TONES.timer} title={title('timer')} />
              <WidgetTray
                icon={TimerIcon}
                title={t('landingV3.tiles.labels.timer')}
                right={
                  <span className="flex items-center gap-1 text-fg-subtle" aria-hidden>
                    <Settings2Icon className="h-4 w-4" />
                    <Maximize2Icon className="h-4 w-4" />
                  </span>
                }>
                <MiniTimer controlSize={stacked ? 'm' : 'xs'} />
              </WidgetTray>
            </>
          )}
        </Tile>
      </div>
      {/* Column 2 */}
      <div className={column}>
        <Tile
          hue={TILE_HUES.friends}
          column={stacked ? 0 : 1}
          layout={layout}
          className={order(2)}>
          {() => (
            <>
              <TileHeader
                icon={ShareIcon}
                tone={TONES.friends}
                title={title('friends')}
              />
              <FriendsGroups />
            </>
          )}
        </Tile>
        <Tile
          hue={TILE_HUES.heat}
          column={stacked ? 0 : 1}
          layout={layout}
          className={order(4)}>
          {() => (
            <>
              <TileHeader icon={FlameIcon} tone={TONES.heat} title={title('heat')} />
              <WidgetTray
                icon={FlameIcon}
                // No scope pill: the frame's Global / University toggle meant
                // nothing on a heat map of one person's days (user decision).
                title={t('landingV3.tiles.labels.heatmap')}>
                {/* Three blocks need 356px; a phone's tile has about 250. */}
                <MiniHeatmap blocks={stacked ? 2 : 3} />
              </WidgetTray>
            </>
          )}
        </Tile>
        <Tile
          hue={TILE_HUES.grades}
          column={stacked ? 0 : 1}
          layout={layout}
          className={cn('flex-1', order(6))}>
          {(arrived) => (
            <>
              <TileHeader
                icon={ChartNoAxesCombinedIcon}
                tone={TONES.grades}
                title={title('grades')}
                mark="*"
                wide
              />
              <GradesChart play={arrived} />
              {/* Figma 104:1315: the claim's source. */}
              <p className="text-[11px] font-bold italic text-secondary-400">
                {t('landingV3.tiles.grades.footnote')}
              </p>
            </>
          )}
        </Tile>
      </div>
    </div>
  );
}
